import type { Locale } from "date-fns";
import { format } from "date-fns";
import { de, enUS } from "date-fns/locale";
import type { FieldErrors } from "react-hook-form";
import type { BookingDetails, Extra, Person } from "@/types/api";
import type { BookingState, CheckoutData, SelectedExtra } from "@/types/booking";

export function buildDemoBookingDetails(state: BookingState): BookingDetails {
  const guests = Object.values(state.travellers)
    .filter(
      (t): t is { first_name: string; last_name: string; birthdate: Date } =>
        !!(t.first_name && t.last_name && t.birthdate),
    )
    .map((t) => ({
      first_name: t.first_name,
      last_name: t.last_name,
      birthdate: format(t.birthdate, "yyyy-MM-dd"),
    }));

  return {
    booking_id: state.bookingId!,
    booking_number: state.bookingNumber!,
    booking_status: "confirmed",
    created_at: state.bookingCreatedAt ?? new Date().toISOString(),
    booking_data: {
      date_from: format(state.dateFrom!, "yyyy-MM-dd"),
      date_until: format(state.dateUntil!, "yyyy-MM-dd"),
      campsites: state.selectedCampsite
        ? [
            {
              id: state.selectedCampsite.id,
              name: state.selectedCampsite.name,
              amount: 1,
              persons: Object.entries(state.selectedPersons)
                .filter(([, amount]) => amount > 0)
                .map(([id, amount]) => ({
                  id: Number(id),
                  name: "",
                  amount,
                  person_type: id,
                })),
            },
          ]
        : [],
      extras: state.selectedExtras,
    },
    first_name: state.billing.name,
    last_name: state.billing.lastname,
    email: state.billing.mail,
    phone: state.billing.phone,
    street1: state.billing.street,
    street2: state.billing.street_additional,
    zip_code: state.billing.zip,
    city: state.billing.city,
    country: state.billing.country.code,
    message: state.billing.message,
    license_plate: state.car.license_plate,
    vehicle_length: state.car.length,
    vehicle_width: state.car.width,
    guests,
    num_persons: Object.values(state.selectedPersons).reduce((sum, n) => sum + n, 0),
    pricelist: state.price!,
  };
}

export function filterExtrasForCampsite(
  selectedExtras: SelectedExtra[],
  campgroundExtras: Extra[],
  campsiteType: string,
): SelectedExtra[] {
  return selectedExtras.filter((extra) => {
    const def = campgroundExtras.find((e) => e.id === extra.id);
    return def?.[`applies_for_${campsiteType}` as keyof Extra] === true;
  });
}

export function buildCheckoutData(state: BookingState, aid: string | null): CheckoutData | null {
  const {
    dateFrom,
    dateUntil,
    selectedCampsite,
    selectedPersons,
    selectedExtras,
    billing,
    car,
    travellers,
    bookingId,
  } = state;

  if (!dateFrom || !dateUntil || !selectedCampsite || Object.keys(selectedPersons).length === 0) {
    return null;
  }

  const guests = Object.values(travellers).map((t) => ({
    first_name: t.first_name!,
    last_name: t.last_name!,
    birthdate: format(t.birthdate!, "yyyy-MM-dd"),
  }));

  return {
    aid,
    old_booking_id: bookingId || undefined,
    date_from: format(dateFrom, "yyyy-MM-dd"),
    date_until: format(dateUntil, "yyyy-MM-dd"),
    campsites: [
      {
        id: selectedCampsite.id,
        amount: 1,
        persons: Object.entries(selectedPersons).map(([id, amount]) => ({
          id: parseInt(id, 10),
          amount,
        })),
      },
    ],
    extras: selectedExtras,
    first_name: billing.name,
    last_name: billing.lastname,
    email: billing.mail,
    phone: billing.phone,
    street1: billing.street,
    street2: billing.street_additional,
    zip_code: billing.zip,
    city: billing.city,
    country: billing.country.code,
    message: billing.message,
    license_plate: car.license_plate,
    vehicle_length: car.length,
    vehicle_width: car.width,
    guests,
    payment_system: state.price?.payment_system,
  };
}
/**
 * Maps i18n locale string to date-fns locale object
 * @param locale - i18n locale string (e.g., "en", "de")
 * @returns date-fns Locale object
 */
export function getDateFnsLocale(locale: string): Locale {
  const localeMap: Record<string, Locale> = {
    en: enUS,
    de: de,
  };
  return localeMap[locale] || enUS;
}

/**
 * Formats a date range into a readable string
 * @param dates - Tuple of start and end dates (can be null)
 * @param locale - i18n locale string (e.g., "en", "de"). Defaults to "en"
 * @returns Formatted date range string (e.g., "01 Jan - 15 Jan") or empty string
 */
export function formatDateRange(dates: [Date | null, Date | null], locale: string = "en"): string {
  if (dates[0] && dates[1]) {
    const dateFnsLocale = getDateFnsLocale(locale);
    return `${format(dates[0], "dd MMM", {
      locale: dateFnsLocale,
    })} - ${format(dates[1], "dd MMM", { locale: dateFnsLocale })}`;
  }
  return "";
}

/**
 * Gets the label for a campsite type by its ID
 * @param campsiteTypeId - The campsite type ID to look up
 * @param campsiteTypes - Array of [value, label] tuples
 * @returns The campsite type label or empty string if not found
 */
export function getCampsiteTypeLabel(
  campsiteTypeId: string | null,
  campsiteTypes: [string, string][],
): string {
  if (!campsiteTypeId) {
    return "";
  }
  const campsiteType = campsiteTypes.find(([value]) => value === campsiteTypeId);
  return campsiteType ? campsiteType[1] : "";
}

export function formatGuests(selectedPersons: Record<number, number>, persons: Person[]): string {
  if (Object.keys(selectedPersons).length === 0 || persons.length === 0) {
    return "";
  }

  const guestParts: string[] = [];
  Object.entries(selectedPersons).forEach(([id, amount]) => {
    const person = persons.find((p) => p.id === parseInt(id, 10));
    if (person && amount > 0) {
      guestParts.push(`${amount} ${person.name}`);
    }
  });
  return guestParts.join(", ");
}

export function getTotalGuests(
  selectedPersons: Record<number, number>,
  persons: Person[],
  type: "human" | "pet" | "adult" | "all" = "all",
): number {
  return persons.reduce((sum, person) => {
    if (
      (type === "human" && person.person_type === "pet") ||
      (type === "pet" && person.person_type !== "pet") ||
      (type === "adult" && person.person_type !== "adult")
    ) {
      return sum;
    }
    return sum + (selectedPersons[person.id] || 0);
  }, 0);
}

/**
 * Returns an array mapping guest index to their Person type.
 * Iterates through persons in order and expands based on selectedPersons counts.
 */
export function getGuestPersons(
  selectedPersons: Record<number, number>,
  persons: Person[],
): Person[] {
  const result: Person[] = [];
  persons.forEach((person) => {
    if (person.person_type === "pet") return;
    const count = selectedPersons[person.id] || 0;
    for (let i = 0; i < count; i++) {
      result.push(person);
    }
  });
  return result;
}

function getFirstErrorPath(errors: FieldErrors, path: string = ""): string | null {
  for (const key of Object.keys(errors)) {
    const error = errors[key];
    const currentPath = path ? `${path}.${key}` : key;

    if (error?.message) {
      return currentPath;
    }

    if (error && typeof error === "object") {
      const nestedPath = getFirstErrorPath(error as FieldErrors, currentPath);
      if (nestedPath) return nestedPath;
    }
  }
  return null;
}

export function scrollToFirstError(errors: FieldErrors) {
  const firstErrorPath = getFirstErrorPath(errors);
  if (!firstErrorPath) return;

  const el = document.querySelector(`[name="${firstErrorPath}"]`);
  el?.scrollIntoView({ behavior: "smooth", block: "center" });
}

export function getTestData(guestsNum: number, personIds: number[] = []) {
  return {
    name: "John",
    lastname: "Doe",
    emails: {
      mail: "tools+checkouttest@campnerd.de",
      confirmMail: "tools+checkouttest@campnerd.de",
    },
    phone: "+49123456789",
    street: "Hauptstraße 25",
    street_additional: "Apartment 3B",
    zip: "10115",
    city: "Berlin",
    country: {
      code: "de",
      label: "Germany",
    },
    message: "Hello, this is a message from the guest",
    car: {
      license_plate: "B 1234",
      length: 6,
      width: 3,
    },
    guests: Array.from({ length: guestsNum }, (_, i) => ({
      person_id: personIds[i] ?? 0,
      first_name: "John",
      last_name: "Doe",
      birthdate: new Date(1990, 2, 15),
    })),
  };
}
